import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Trash2, CornerDownRight, Image as ImageIcon, AudioLines } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { getRelativeTime, formatMilliSeconds } from '../../../util/UtilFunctions';
import { deleteMessage } from '../../../apiServices/messageApi';
import { deleteTask } from '../../../apiServices/taskApi';
import { removeMessage, removeTask, setSelectedItem } from '../../../app/features/project/projectSlice';
import { lineTypes } from '../lib/lineMath';

interface MessageTask {
  id: string;
  resource: string;
  type: string;
  taskType: string;
  timestamp?: number;
  owner: string;
  messages: { content: string; created: string; sender: string; userData?: UserData }[];
}

interface UserData {
  given_name?: string;
  family_name?: string;
  img?: string;
}

interface MessageImage {
  task: boolean;
  imageUrl?: string;
}

interface MessageProps {
  firstName?: string;
  lastName?: string;
  created: string;
  content: string;
  image?: MessageImage;
  isAReply: boolean;
  messageId?: string;
  projectId: string;
  isRemovable: boolean;
  task?: MessageTask;
  taskResource?: { id: string; thumbnailUrl?: string };
  previewItemOnShow?: { id: string };
  updateSelectedTask?: (task: MessageTask) => void;
  updateModalOpen: (type: string, params?: Record<string, unknown>) => void;
}

export function Message({
  firstName,
  lastName,
  created,
  content,
  image,
  isAReply,
  messageId,
  projectId,
  isRemovable,
  task,
  taskResource,
  previewItemOnShow,
  updateSelectedTask,
  updateModalOpen,
}: MessageProps) {
  const [deleting, setDeleting] = useState(false);
  const dispatch = useDispatch();

  const name = firstName && lastName ? `${firstName} ${lastName}` : '[Removed user]';
  const relativeTime = getRelativeTime(new Date(), new Date(created));

  const handleImageClick = () => {
    if (previewItemOnShow && taskResource && task) {
      if (previewItemOnShow.id !== taskResource.id) {
        dispatch(setSelectedItem(taskResource));
      }
      updateSelectedTask?.(task);
      window.scrollTo(0, 0);
    }
  };

  const handleDelete = () => {
    const deleteFunc = () =>
      new Promise<void>(async (resolve, reject) => {
        setDeleting(true);
        try {
          if (task) {
            await deleteTask(task.id, projectId);
            dispatch(removeTask(task.id));
          } else if (messageId) {
            await deleteMessage(messageId, projectId);
            dispatch(removeMessage(messageId));
          }
          resolve();
        } catch (err) {
          console.error(err);
          setDeleting(false);
          reject(err);
        }
      });
    updateModalOpen('deleteItem', { action: deleteFunc });
  };

  const parseContent = (text: string) =>
    text.split('\\n').map((line, i) =>
      line === '' ? (
        <br key={i} />
      ) : (
        <p key={i} className="text-sm text-accent-foreground leading-relaxed">
          {line.replace(/\\"/g, '"')}
        </p>
      ),
    );

  // Task thumbnail image section
  const renderTaskImage = () => {
    if (!task) return null;
    const isLine = Object.values(lineTypes).includes(task.taskType);
    return (
      <div
        className={`relative size-10 shrink-0 cursor-pointer overflow-hidden rounded ${isLine ? 'border border-info' : ''}`}
        onClick={handleImageClick}
      >
        <div className="absolute inset-0 flex items-end justify-center bg-black/30 text-[10px] text-white z-10">
          {task.type !== 'image' ? (
            <span>{formatMilliSeconds(task.timestamp)}</span>
          ) : (
            <ImageIcon className="size-3" />
          )}
        </div>
        {task.type === 'audio' ? (
          <div className="flex items-center justify-center size-full bg-secondary">
            <AudioLines className="size-5 text-muted-foreground" />
          </div>
        ) : image?.imageUrl ? (
          <img src={image.imageUrl} alt="task preview" className="size-full object-cover" />
        ) : (
          <div className="size-full bg-accent" />
        )}
      </div>
    );
  };

  return (
    <div className={`flex gap-3 px-3 py-2 ${isAReply ? 'pl-12' : ''}`}>
      {/* Image/avatar section */}
      <div className="flex shrink-0 items-start gap-1">
        {isAReply && <CornerDownRight className="size-3.5 text-muted-foreground mt-1" />}
        {image?.task ? (
          renderTaskImage()
        ) : (
          <Avatar src={image?.imageUrl} name={name} size="sm" />
        )}
      </div>

      {/* Content section */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-foreground truncate">{name}</h4>
          <span className="text-xs text-muted-foreground shrink-0">{relativeTime}</span>
          {isRemovable && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="ml-auto text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
              title="remove"
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
        </div>
        <div className="mt-0.5">{parseContent(content)}</div>
      </div>
    </div>
  );
}
